import {
  memo,
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useContext,
} from "react";
import Tree from "./tree";
import reducer from "./reducer";
import { FORCE_UPDATE, RELOAD_DATA } from "./actions";
import htmlParser, { getFormQuestions } from "./parser";
import teaserParser from "./parser/teaser-parser";
import { Spin, message } from "antd";
import Side from "./Side";
import Content from "./Content";
import Header from "./Header";
import { DEF_SIDE_CONTEINT_CONF } from "./constanst";
import { setAccessToken } from "./Service/accessToken";
export const Context = createContext({});

export const Provider = memo((props) => {
  const {
    data,
    onChange,
    editorType: initeditorType,
    hideQuestionField,
    mergeTag,
    contentFormData,
  } = props;

  const tree = new Tree(data);

  const shareRefs = useRef({
    subscriptions: [],
    tree,
  });

  const contentAreaRef = useRef();
  const editorRef = useRef();
  const editorTypeRef = useRef(initeditorType);
  const editorOptionsRef = useRef({});

  const [state, dispatch] = useReducer(reducer, tree);

  // Improve performance, prevent diff virtual tree,  but you must manual update component.
  // eg: useUpdate().
  const changeChildrenPos = useCallback((children, oldIndex, newIndex) => {
    if (newIndex < 0 || newIndex >= children.length) return;
    const temp = children[oldIndex];
    children[oldIndex] = children[newIndex];
    children[newIndex] = temp;
  }, []);

  const removeChild = useCallback((children, id) => {
    const foundIndex = children.findIndex((n) => n.id === id);
    children.splice(foundIndex, 1);
  }, []);

  const addChild = useCallback((node, childData, index) => {
    node.addChild(childData, index);
  }, []);

  const addSubscriber = useCallback(
    (sub) => {
      shareRefs.current.subscriptions.push(sub);
    },
    [shareRefs]
  );

  const removeSubscriber = useCallback((fn) => {
    const foundIndex = shareRefs.current.subscriptions.findIndex((item) => {
      return item.fn === fn;
    });
    shareRefs.current.subscriptions.splice(foundIndex, 1);
  }, []);

  useEffect(() => {
    editorRef.current = {
      getJSON: () => {
        if (editorTypeRef.current === "html") {
          shareRefs.current.tree.root.data.html = window._FULL_CK_.getData();
        }
        return shareRefs.current.tree.root.toJSON();
      },
      getHTML: (isPreview) => {
        if (editorTypeRef.current === "html") {
          return window._FULL_CK_.getData();
        } else if (editorTypeRef.current === "teaser") {
          return teaserParser(shareRefs.current.tree, isPreview);
        }

        return htmlParser(shareRefs.current.tree, isPreview);
      },
      getData: () => {
        const html =
          editorTypeRef.current === "wechat"
            ? contentAreaRef.current?.outerHTML
            : htmlParser(shareRefs.current.tree);
        return {
          json_file: shareRefs.current.tree.root.toJSON(),
          html_file: html,
        };
      },
      getValidData: async () => {
        if (shareRefs.current.contentForm) {
          try {
            await shareRefs.current.contentConfigForm?.validateFields();
            await shareRefs.current.contentForm.validateFields();
            const contentFormData =
              shareRefs.current.contentForm.getFieldsValue();
            const html =
              editorTypeRef.current === "wechat"
                ? contentAreaRef.current?.outerHTML
                : htmlParser(shareRefs.current.tree);

            // parse trackable link in html
            const linkSet = new Set();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, "text/html");
            // trackable qrcode link
            doc.querySelectorAll("img[rdc-t-qr]").forEach((it) => {
              linkSet.add(it.getAttribute("rdc-t-url"));
            });
            // wechat outer link
            doc.querySelectorAll("a").forEach((it) => {
              linkSet.add(it.href);
            });

            return {
              ...contentFormData,
              json_file: shareRefs.current.tree.root.toJSON(),
              html_file: html,
              links: [...linkSet],
            };
          } catch (e) {
            message.error("form.msg.err.field");
            return null;
          }
        }
        return null;
      },
      getContentFormData: () => {},
      setData: (json) => {
        const newTree = new Tree(json);
        shareRefs.current.tree = newTree;
        shareRefs.current.selectedNode = null;
        dispatch({ action: RELOAD_DATA, data: newTree });
      },
      getFormQuestions: () => {
        return getFormQuestions(shareRefs.current.tree);
      },
    };
  }, [shareRefs]);

  const notifySubscriber = useCallback(
    (type) => {
      shareRefs.current.subscriptions
        .filter((item) => item.type === type)
        .forEach((item) => {
          if (item && item.fn) {
            return item.fn();
          }
        });
    },
    [shareRefs]
  );

  const triggerChange = useCallback(() => {
    if (onChange) {
      const data = editorRef.current.getJSON();
      onChange(data);
    } else {
      console.warn("[H5 Editor]: onChange not define");
    }
  }, [onChange]);

  const onReceiveMessage = useCallback((event) => {
    const data = event.data;
    if (data.action === "init") {
      console.log("Init editor", data);
      editorOptionsRef.current = data.data;
      editorTypeRef.current = data.data.editorType;
      editorRef.current.setData(data.data.data);
      if (data.data.requestInfo) {
        shareRefs.current.requestInfo = data.data.requestInfo;
      }
      dispatch({ action: FORCE_UPDATE, data: null })
      localStorage.setItem("fileKey", data.data.filekey)
      localStorage.setItem("apiDomain", data.data.apiDomain)
      localStorage.setItem("apiKey", data.data.apiKey)
      setAccessToken(data.data.requestInfo?.token || "")
    }
    if (data.action === "accessToken") {
      setAccessToken(data.token || "")
    }
    if (data.action === "load") {
      console.log("Ready load template", data);
      editorRef.current.setData(data.data);
    }
    if (data.action === "save" || data.action === "preview" || data.action === "changeEditorType") {
      const jsonFile = editorRef.current.getJSON();
      const htmlFile = editorRef.current.getHTML();
      const questions = getFormQuestions(shareRefs.current.tree);
      window.parent.postMessage({ type: data.action, jsonFile, htmlFile, questions, pageType: data.data },'*')
    }
    if (data.action === "updateSideContent") {
      editorOptionsRef.current.sideContent = data.data;
    }

  }, []);

  useEffect(() => {
    window.addEventListener("message", onReceiveMessage, false);
    addSubscriber({ type: "DATA_CHANGE", fn: triggerChange });
  }, []);

  console.log(
    "side conf: ",
    editorOptionsRef.current.sideContent || DEF_SIDE_CONTEINT_CONF
  );

  const editorContext = {
    editorType: editorTypeRef.current,
    sideContent: editorOptionsRef.current.sideContent || DEF_SIDE_CONTEINT_CONF,
    state,
    hideHeader: !!editorOptionsRef.current.hideHeader,
    editorOptions: editorOptionsRef.current,
    hideQuestionField,
    dispatch,
    changeChildrenPos,
    addChild,
    removeChild,
    addSubscriber,
    removeSubscriber,
    notifySubscriber,
    contentAreaRef,
    mergeTag,
    contentFormData,
    editorRef: editorRef,
    shareRefs: shareRefs.current,
  };

  if (!shareRefs.current.tree.root)
    return (
      <Spin
        spinning
        style={{ position: "fixed", top: 0, bottom: 0, right: 0, left: 0 }}
      />
    );

  return (
    <Context.Provider value={editorContext}>
      <Header />
      <div
        className="row"
        style={{ display: "flex", justifyContent: "center" }}
      >
        <Content />
        {editorTypeRef.current !== "html" && (
          <div className="col-xs-1 silder">
            <Side />
          </div>
        )}
      </div>
    </Context.Provider>
  );
});

export const useEditorContext = () => {
  return useContext(Context);
};

export const { Consumer } = Context;
